import { downloadBlobAs, downloadText } from "@/features/preflight/lib/download";

import type {
  PersonaScriptDto,
  PersonaScriptHookDto,
  PersonaScriptSceneDto,
} from "../api/persona-opportunity-scripts.api.js";
import {
  formatPersonaScriptDocument,
  formatPersonaScriptForCopy,
  type ScriptDocumentLabels,
} from "./persona-script-clipboard.js";
import {
  DOCX_FETCH_TIMEOUT_MS,
  DOCX_FONT_BODY,
  DOCX_FONT_DISPLAY,
  DOCX_FONT_MEDIUM,
  DOCX_FONT_RTL,
  loadDocxFonts,
  type DocxEmbeddedFont,
} from "./persona-script-docx-fonts.js";
import { getPersonaScriptDisplayTitle } from "./script-display-title.js";

export const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Same break the plain-text export inserts between selected documents. */
export const DOCUMENT_EXPORT_SEPARATOR = "---";

export type PersonaDocumentExportFormat = "txt" | "docx";

export interface ScriptDocxLabels {
  sequence: string;
  concept: string;
  duration: string;
  presenter: string;
  background: string;
  product: string;
  scene: string;
  seeing: string;
  hearing: string;
  props: string;
  whyThisWorks: string;
  /** Heading over the scenes table. */
  scenes: string;
  /** Heading over the full script body. */
  script: string;
  /** Heading over the alternate hooks block. */
  hooksTitle: string;
  /** "Hook {{index}} (primary)" — the first hook in the block. */
  primaryHook: string;
  /** "Hook {{index}}" — every other hook. */
  hook: string;
  voiceover: string;
  onScreenText: string;
  /** Footer: "Page {{current}} of {{total}}" split around the two page fields. */
  pageOf: string;
  /** Eyebrow over a brief's title. */
  brief: string;
  /** Section title over the sketch storyboard sheet(s). */
  storyboard: string;
}

/** Matches the clipboard dump, except rationale is labeled for production handoff. */
export const DEFAULT_SCRIPT_DOCX_LABELS: ScriptDocxLabels = {
  sequence: "Sequence",
  concept: "Concept",
  duration: "Duration",
  presenter: "Presenter",
  background: "Background",
  product: "Product",
  scene: "Scene",
  seeing: "Seeing",
  hearing: "Hearing",
  props: "Props",
  whyThisWorks: "Why this works",
  scenes: "Scenes",
  script: "Script",
  hooksTitle: "Scene 1 hooks",
  primaryHook: "Hook {{index}} (primary)",
  hook: "Hook {{index}}",
  voiceover: "Voiceover",
  onScreenText: "On-screen text",
  pageOf: "{{current}} / {{total}}",
  brief: "Brief",
  storyboard: "Storyboard",
};

export interface ScriptDocxPart {
  kind: "script";
  script: PersonaScriptDto;
  untitled: string;
  labels?: ScriptDocxLabels;
}

export interface BriefDocxPart {
  kind: "brief";
  text: string;
  /** Eyebrow and footer wording; callers pass `scriptDocxLabels(t)`. */
  labels?: ScriptDocxLabels;
}

export type PersonaDocxPart = ScriptDocxPart | BriefDocxPart;

export interface BuildDocxOptions {
  /** Fonts to embed. Defaults to none; the download helpers pass `loadDocxFonts()`. */
  fonts?: readonly DocxEmbeddedFont[];
  /**
   * Fetches a storyboard image by URL. Resolve `null` (or reject) to leave that
   * image out. Defaults to none, so the Word file has no pictures unless the
   * caller can load them; the download helpers pass `fetchImageBytes`.
   */
  loadImage?: (url: string) => Promise<Uint8Array | null>;
}

/** Best-effort browser fetch of an image's bytes. Any failure or timeout means "no picture". */
export async function fetchImageBytes(url: string): Promise<Uint8Array | null> {
  try {
    const response = await fetch(url, { signal: AbortSignal.timeout(DOCX_FETCH_TIMEOUT_MS) });
    if (!response.ok) return null;
    return new Uint8Array(await response.arrayBuffer());
  } catch {
    return null;
  }
}

const FILENAME_UNSAFE = /[\\/:*?"<>|]/g;
const FILENAME_WHITESPACE = /\s+/g;
const FILENAME_BIDI_AND_CONTROL =
  /[\u0000-\u001F\u007F-\u009F\u200E\u200F\u202A-\u202E\u2066-\u2069\uFEFF\uFFFE\uFFFF]/g;
const FILENAME_TRAILING_DOTS = /\.+$/u;
const FILENAME_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
/** Tenant and title together, before the date. */
const MAX_DOCUMENT_STEM_LENGTH = 50;

/** XML 1.0 illegal chars, plus the noncharacters Word rejects inside a text run. */
const DOCX_ILLEGAL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;
const RTL_CHARS = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFE]/;

/** A script line that only names a beat, e.g. "[HOOK — 0-4s]". */
const SCRIPT_BEAT_MARKER = /^\[([^\]]+)\]$/;
/** A script line that is only a stage direction, e.g. "(Cut to: end card.)". */
const SCRIPT_STAGE_DIRECTION = /^\([^)]*\)$/;
/** "MAN (to camera): line" or "VO: line" — a speaker tag in caps before the first colon. */
const SCRIPT_SPEAKER_LINE = /^([A-Z][A-Z0-9 .'&/-]{0,28}?(?:\s*\([^)]{0,80}\))?):\s+(\S.*)$/;
/** Where a cell's prose enumerates shots: "… (1) Medium shot … (2) Cut to …". */
const CELL_ENUMERATOR_BREAK = /\s+(?=\(\d{1,2}\)\s)/;
/** Sentence end followed by a new sentence; keeps closing quotes and brackets with the sentence. */
const CELL_SENTENCE_BREAK = /(?<=[.!?][)"'”’]?)\s+(?=[A-Z("“'‘])/;
/** A prose cell longer than this is split into sentence groups. */
const CELL_LONG_TEXT = 220;
/** Sentences are grouped until a paragraph reaches this length. */
const CELL_PARAGRAPH_TARGET = 140;

// ---------------------------------------------------------------------------
// Page geometry (DXA, twentieths of a point) and the type scale (half-points).
// The look follows the Veil report surface: quiet neutral chrome, hierarchy
// by size not weight, labels as small tracked eyebrows, rows as soft insets.
// ---------------------------------------------------------------------------

const PAGE = { width: 11906, height: 16838 }; // A4
const MARGIN = { top: 1418, right: 1361, bottom: 1247, left: 1361 };
const CONTENT_WIDTH = PAGE.width - MARGIN.left - MARGIN.right; // 9184
const FOOTER_OFFSET = 640;
/** A long hook as the footer title would wrap; cut it to one line. */
const FOOTER_TITLE_MAX = 72;

/** Wide lists run to the page edge, leaving this gutter on each side. */
const BLEED_GUTTER = 300;
const BLEED_WIDTH = PAGE.width - 2 * BLEED_GUTTER; // 11306
const BLEED_INDENT = BLEED_GUTTER - MARGIN.left; // negative: start left of the text column

const DETAIL_LABEL_WIDTH = 1900;
const SCENE_NUMBER_WIDTH = 420;
const SCENE_NAME_WIDTH = 1180;
/** Share of the remaining scene-table width per text column, when all four are present. */
const SCENE_SHARES = { seeing: 0.38, hearing: 0.26, product: 0.17, props: 0.19 } as const;
const ROW_GAP = 90; // the gap between inset rows
/** Word lays images out in CSS pixels at 96 dpi; 15 DXA per pixel. */
const DXA_PER_PIXEL = 15;
const STORYBOARD_WIDTH_PX = Math.floor(BLEED_WIDTH / DXA_PER_PIXEL);
const CELL_PAD = { top: 150, bottom: 160, left: 180, right: 180 };

const SIZE = {
  title: 52, // 26pt, display face
  lede: 22, // 11pt
  section: 26, // 13pt
  body: 20, // 10pt
  cell: 19, // 9.5pt
  eyebrow: 15, // 7.5pt, all caps, tracked
  footer: 17, // 8.5pt
} as const;

/** Veil inks. Ink-2 is the label colour; ink-3 is decorative only. */
const INK = "1A1A1A";
const INK_2 = "6E6C66";
const INK_3 = "8E8C86";
/** rgba(34,30,24,.045) composited on white — the inset row fill. */
const INSET_FILL = "F4F3F0";

const LINE_BODY = 312; // 1.3 × single
const LINE_TIGHT = 276; // 1.15 × single, for the title

export function scriptDocxLabels(
  t: (key: string, defaultValue: string) => string,
): ScriptDocxLabels {
  return {
    sequence: t("personaOpportunities.scripts.docx.sequence", "Sequence"),
    concept: t("personaOpportunities.scripts.docx.concept", "Concept"),
    duration: t("personaOpportunities.scripts.docx.duration", "Duration"),
    presenter: t("personaOpportunities.scripts.docx.presenter", "Presenter"),
    background: t("personaOpportunities.scripts.docx.background", "Background"),
    product: t("personaOpportunities.scripts.docx.product", "Product"),
    scene: t("personaOpportunities.scripts.docx.scene", "Scene"),
    seeing: t("personaOpportunities.scripts.docx.seeing", "Seeing"),
    hearing: t("personaOpportunities.scripts.docx.hearing", "Hearing"),
    props: t("personaOpportunities.scripts.docx.props", "Props"),
    whyThisWorks: t("personaOpportunities.scripts.docx.whyThisWorks", "Why this works"),
    scenes: t("personaOpportunities.scripts.docx.scenes", "Scenes"),
    script: t("personaOpportunities.scripts.docx.script", "Script"),
    hooksTitle: t("personaOpportunities.scripts.hooksBlock.title", "Scene 1 hooks"),
    primaryHook: t("personaOpportunities.scripts.hooksBlock.primaryHook", "Hook {{index}} (primary)"),
    hook: t("personaOpportunities.scripts.hooksBlock.hook", "Hook {{index}}"),
    voiceover: t("personaOpportunities.scripts.hooksBlock.voiceover", "Voiceover"),
    onScreenText: t("personaOpportunities.scripts.hooksBlock.onScreenText", "On-screen text"),
    pageOf: t("personaOpportunities.scripts.docx.pageOf", "{{current}} / {{total}}"),
    brief: t("personaOpportunities.scripts.docx.brief", "Brief"),
    storyboard: t("personaOpportunities.scripts.docx.storyboard", "Storyboard"),
  };
}

function toDocumentLabels(labels: ScriptDocxLabels): ScriptDocumentLabels {
  return {
    sequence: labels.sequence,
    concept: labels.concept,
    duration: labels.duration,
    presenter: labels.presenter,
    background: labels.background,
    product: labels.product,
    scene: labels.scene,
    seeing: labels.seeing,
    hearing: labels.hearing,
    props: labels.props,
    rationale: labels.whyThisWorks,
  };
}

/**
 * Plain-text body for one script with the Word labels (rationale is
 * "Why this works" because production readers do not know the product term).
 * The Word file itself is laid out by `buildPersonaDocumentsDocx`; this stays
 * the shared source of the field order and the title rule.
 */
export function formatPersonaScriptForDocx(
  script: PersonaScriptDto,
  untitled: string,
  labels: ScriptDocxLabels = DEFAULT_SCRIPT_DOCX_LABELS,
): string {
  return formatPersonaScriptDocument(script, untitled, toDocumentLabels(labels));
}

/** Drops characters that make word/document.xml not well-formed, including lone surrogates. */
export function sanitizeDocxText(value: string): string {
  let cleaned = "";
  for (const char of value) {
    if (isLoneSurrogate(char)) continue;
    cleaned += char;
  }
  return cleaned.replace(DOCX_ILLEGAL_CHARS, "");
}

function isLoneSurrogate(char: string): boolean {
  if (char.length !== 1) return false;
  const code = char.charCodeAt(0);
  return code >= 0xd800 && code <= 0xdfff;
}

function lineNeedsRtl(value: string): boolean {
  return RTL_CHARS.test(value);
}

export function formatPersonaExportText(
  parts: readonly PersonaDocxPart[],
  format: PersonaDocumentExportFormat,
): string {
  return parts
    .map((part) =>
      part.kind === "brief"
        ? part.text
        : format === "docx"
          ? formatPersonaScriptForDocx(part.script, part.untitled, part.labels)
          : formatPersonaScriptForCopy(part.script, part.untitled),
    )
    .join(`\n\n${DOCUMENT_EXPORT_SEPARATOR}\n\n`);
}

/** Local calendar date as YYYY-MM-DD, so the export name says when it was made. */
function localIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function personaSelectionFilename(
  scriptCount: number,
  briefCount: number,
  format: PersonaDocumentExportFormat,
  date: Date = new Date(),
): string {
  const stem =
    scriptCount > 0 && briefCount > 0
      ? "Scripts and briefs"
      : briefCount > 0
        ? "Briefs"
        : "Scripts";
  return `${stem} ${localIsoDate(date)}.${format === "docx" ? "docx" : "txt"}`;
}

function downloadStem(title: string, fallback: string): string {
  const collapsed = title.replace(FILENAME_WHITESPACE, " ");
  const stripped = collapsed.replace(FILENAME_BIDI_AND_CONTROL, "").replace(FILENAME_UNSAFE, "");
  const sliced = Array.from(stripped).slice(0, MAX_DOCUMENT_STEM_LENGTH).join("");
  const trimmed = sliced.trim().replace(FILENAME_TRAILING_DOTS, "").trim();
  if (trimmed.length === 0 || FILENAME_RESERVED.test(trimmed)) return fallback;
  return trimmed;
}

export interface PersonaDocumentFilenameParts {
  /** Client (tenant) name; leads the name when known. */
  tenant?: string | null;
  /** Script or brief title as shown on screen. */
  title: string;
}

/**
 * "<tenant> - <title> <date>.<ext>" for one document. Tenant and title together
 * are cut to 50 characters; a name that sanitises to nothing falls back to
 * "Script" or "Brief".
 */
export function personaDocumentFilename(
  parts: PersonaDocumentFilenameParts,
  kind: PersonaDocxPart["kind"],
  format: PersonaDocumentExportFormat,
  date: Date = new Date(),
): string {
  const joined = [parts.tenant ?? "", parts.title]
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(" - ");
  const stem = downloadStem(joined, kind === "script" ? "Script" : "Brief");
  return `${stem} ${localIsoDate(date)}.${format === "docx" ? "docx" : "txt"}`;
}

// ---------------------------------------------------------------------------
// Writer
// ---------------------------------------------------------------------------

type DocxModule = typeof import("docx");
type DocxParagraph = InstanceType<DocxModule["Paragraph"]>;
type DocxTable = InstanceType<DocxModule["Table"]>;
type DocxTableRow = InstanceType<DocxModule["TableRow"]>;
type DocxBlock = DocxParagraph | DocxTable;
type DocxSection = NonNullable<ConstructorParameters<DocxModule["Document"]>[0]["sections"]>[number];
type DocxDocumentFont = NonNullable<ConstructorParameters<DocxModule["Document"]>[0]["fonts"]>[number];

type Face = "body" | "medium" | "display";

interface RunStyle {
  face?: Face;
  size?: number;
  color?: string;
  /** All caps with +0.09em tracking: the Veil eyebrow. */
  eyebrow?: boolean;
  /** Tracking in twentieths of a point; negative tightens. */
  tracking?: number;
}

interface ParagraphStyle extends RunStyle {
  styleId?: "Title" | "Heading1";
  before?: number;
  after?: number;
  line?: number;
  keepNext?: boolean;
  align?: "left" | "right";
}

type Piece = readonly [text: string, style?: RunStyle];
/** One table cell: a list of paragraphs, each a list of styled pieces. */
type CellText = ReadonlyArray<readonly Piece[]>;

interface ImageInfo {
  type: "png" | "jpg" | "gif";
  width: number;
  height: number;
}

/** Tracking for an eyebrow of a given size: +0.09em, in twentieths of a point. */
function eyebrowTracking(sizeHalfPoints: number): number {
  return Math.round((sizeHalfPoints / 2) * 0.09 * 20);
}

/** Tracking for a display line of a given size: −0.03em. */
function displayTracking(sizeHalfPoints: number): number {
  return -Math.round((sizeHalfPoints / 2) * 0.03 * 20);
}

function faceName(face: Face | undefined): string {
  if (face === "display") return DOCX_FONT_DISPLAY;
  if (face === "medium") return DOCX_FONT_MEDIUM;
  return DOCX_FONT_BODY;
}

class DocxWriter {
  constructor(private readonly docx: DocxModule) {}

  /** One paragraph from text pieces. Empty pieces are dropped; Hebrew or Arabic flips the direction. */
  paragraph(pieces: readonly Piece[], style: ParagraphStyle = {}): DocxParagraph {
    const { AlignmentType, HeadingLevel, LineRuleType, Paragraph, TextRun } = this.docx;
    const cleaned = pieces
      .map(([text, run]) => [sanitizeDocxText(text), run] as const)
      .filter(([text]) => text.length > 0);
    const rtl = cleaned.some(([text]) => lineNeedsRtl(text));
    const children = cleaned.map(([text, run]) => {
      const merged: RunStyle = { ...style, ...run };
      const size = merged.size ?? SIZE.body;
      const eyebrow = merged.eyebrow === true;
      const family = faceName(eyebrow && !merged.face ? "medium" : merged.face);
      const tracking = merged.tracking ?? (eyebrow ? eyebrowTracking(size) : undefined);
      return new TextRun({
        text,
        size,
        color: merged.color ?? INK,
        font: { ascii: family, hAnsi: family, eastAsia: family, cs: DOCX_FONT_RTL },
        allCaps: eyebrow || undefined,
        characterSpacing: tracking,
        rightToLeft: rtl || undefined,
      });
    });
    return new Paragraph({
      children,
      heading:
        style.styleId === "Title"
          ? HeadingLevel.TITLE
          : style.styleId === "Heading1"
            ? HeadingLevel.HEADING_1
            : undefined,
      bidirectional: rtl || undefined,
      alignment: style.align === "right" ? AlignmentType.RIGHT : undefined,
      keepNext: style.keepNext,
      spacing: {
        before: style.before ?? 0,
        after: style.after ?? 0,
        line: style.line ?? LINE_BODY,
        lineRule: LineRuleType.AUTO,
      },
    });
  }

  text(text: string, style: ParagraphStyle = {}): DocxParagraph {
    return this.paragraph([[text]], style);
  }

  eyebrow(text: string, style: ParagraphStyle = {}): DocxParagraph {
    return this.text(text, { size: SIZE.eyebrow, color: INK_2, eyebrow: true, ...style });
  }

  sectionTitle(text: string): DocxParagraph {
    return this.text(text, {
      styleId: "Heading1",
      face: "medium",
      size: SIZE.section,
      tracking: -Math.round((SIZE.section / 2) * 0.025 * 20),
      before: 520,
      after: 200,
      keepNext: true,
    });
  }

  /** Label column as eyebrows, value column as body text. No rules, no fill. */
  detailList(rows: ReadonlyArray<readonly [label: string, value: string]>): DocxTable {
    const { Table, TableBorders, TableLayoutType, WidthType } = this.docx;
    const valueWidth = CONTENT_WIDTH - DETAIL_LABEL_WIDTH;
    return new Table({
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      columnWidths: [DETAIL_LABEL_WIDTH, valueWidth],
      layout: TableLayoutType.FIXED,
      borders: TableBorders.NONE,
      rows: rows.map(([label, value]) =>
        this.row(
          [
            // The eyebrow is smaller than the value; the extra top padding puts them on one baseline.
            [DETAIL_LABEL_WIDTH, [this.eyebrow(label)], { top: 112, bottom: 70, left: 0, right: 120 }],
            [valueWidth, [this.text(value)], { top: 70, bottom: 70, left: 0, right: 0 }],
          ],
        ),
      ),
    });
  }

  /**
   * Veil list: eyebrow header labels, then each record as a soft inset row
   * with a thin white gap between rows. No borders anywhere.
   */
  insetList(
    header: readonly string[],
    body: ReadonlyArray<ReadonlyArray<CellText>>,
    widths: readonly number[],
    options: { bleed?: boolean } = {},
  ): DocxTable {
    const { Table, TableBorders, TableLayoutType, WidthType } = this.docx;
    const width = widths.reduce((sum, w) => sum + w, 0);
    const rows: DocxTableRow[] = [
      this.row(
        header.map((label, index) => [
          widths[index] ?? 0,
          [this.eyebrow(label)],
          { top: 0, bottom: 110, left: CELL_PAD.left, right: CELL_PAD.right },
        ]),
        { header: true },
      ),
    ];
    body.forEach((cells, rowIndex) => {
      if (rowIndex > 0) rows.push(this.gapRow(widths.length));
      rows.push(
        this.row(
          cells.map((paragraphs, index) => [
            widths[index] ?? 0,
            paragraphs.map((pieces, paragraphIndex) =>
              this.paragraph(pieces, {
                size: SIZE.cell,
                // Space between paragraphs inside a cell, none after the last.
                after: paragraphIndex < paragraphs.length - 1 ? 110 : 0,
              }),
            ),
            CELL_PAD,
          ]),
          { fill: INSET_FILL },
        ),
      );
    });
    return new Table({
      width: { size: width, type: WidthType.DXA },
      columnWidths: [...widths],
      indent: options.bleed ? { size: BLEED_INDENT, type: WidthType.DXA } : undefined,
      layout: TableLayoutType.FIXED,
      borders: TableBorders.NONE,
      rows,
    });
  }

  /** A picture on its own line, scaled to `widthPx`, bleeding to the page edge like the lists. */
  image(bytes: Uint8Array, info: ImageInfo, widthPx: number): DocxParagraph {
    const { ImageRun, Paragraph } = this.docx;
    const heightPx = Math.round((widthPx * info.height) / info.width);
    return new Paragraph({
      indent: { left: BLEED_INDENT },
      spacing: { before: 0, after: 200 },
      keepLines: true,
      children: [
        new ImageRun({
          type: info.type,
          data: bytes,
          transformation: { width: widthPx, height: heightPx },
        }),
      ],
    });
  }

  /** One soft inset block holding several paragraphs: the "Why this works" card. */
  insetCard(children: readonly DocxParagraph[]): DocxTable {
    const { Table, TableBorders, TableLayoutType, WidthType } = this.docx;
    return new Table({
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      columnWidths: [CONTENT_WIDTH],
      layout: TableLayoutType.FIXED,
      borders: TableBorders.NONE,
      rows: [
        this.row(
          [[CONTENT_WIDTH, children, { top: 260, bottom: 300, left: 300, right: 300 }]],
          { fill: INSET_FILL },
        ),
      ],
    });
  }

  private gapRow(columns: number): DocxTableRow {
    const { HeightRule, Paragraph, TableCell, TableRow, TextRun } = this.docx;
    return new TableRow({
      height: { value: ROW_GAP, rule: HeightRule.EXACT },
      children: Array.from({ length: columns }, () =>
        new TableCell({
          margins: { top: 0, bottom: 0, left: 0, right: 0 },
          children: [
            new Paragraph({
              spacing: { before: 0, after: 0, line: 100 },
              children: [new TextRun({ text: "", size: 4 })],
            }),
          ],
        }),
      ),
    });
  }

  private row(
    cells: ReadonlyArray<
      readonly [
        width: number,
        content: readonly DocxParagraph[],
        margins: { top: number; bottom: number; left: number; right: number },
      ]
    >,
    options: { header?: boolean; fill?: string } = {},
  ): DocxTableRow {
    const { ShadingType, TableCell, TableRow, VerticalAlign, WidthType } = this.docx;
    return new TableRow({
      tableHeader: options.header,
      cantSplit: true,
      children: cells.map(
        ([width, content, margins]) =>
          new TableCell({
            width: { size: width, type: WidthType.DXA },
            verticalAlign: VerticalAlign.TOP,
            margins,
            shading: options.fill
              ? { type: ShadingType.CLEAR, color: "auto", fill: options.fill }
              : undefined,
            children: [...content],
          }),
      ),
    });
  }

  /** Footer: the document title on the left, "current / total" on the right. */
  footer(title: string, pageOf: string) {
    const { Footer, PageNumber, Paragraph, Tab, TabStopType, TextRun } = this.docx;
    const [beforeCurrent = "", rest = ""] = pageOf.split("{{current}}");
    const [between = "", afterTotal = ""] = rest.split("{{total}}");
    const font = { ascii: DOCX_FONT_BODY, hAnsi: DOCX_FONT_BODY, eastAsia: DOCX_FONT_BODY, cs: DOCX_FONT_RTL };
    const cleanTitle = truncate(sanitizeDocxText(title), FOOTER_TITLE_MAX);
    const run = (children: ReadonlyArray<string | InstanceType<typeof Tab> | (typeof PageNumber)[keyof typeof PageNumber]>) =>
      new TextRun({
        children: [...children],
        size: SIZE.footer,
        color: INK_3,
        font,
        rightToLeft: lineNeedsRtl(cleanTitle) || undefined,
      });
    return new Footer({
      children: [
        new Paragraph({
          tabStops: [{ type: TabStopType.RIGHT, position: CONTENT_WIDTH }],
          spacing: { before: 0, after: 0, line: 240 },
          children: [
            run([cleanTitle, new Tab(), beforeCurrent]),
            run([PageNumber.CURRENT]),
            run([between]),
            run([PageNumber.TOTAL_PAGES]),
            run([afterTotal]),
          ],
        }),
      ],
    });
  }
}

// ---------------------------------------------------------------------------
// Script layout
// ---------------------------------------------------------------------------

function applyHookIndex(template: string, index: number): string {
  return template.replaceAll("{{index}}", String(index));
}

function trimmed(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

function truncate(value: string, max: number): string {
  const chars = Array.from(value.trim());
  if (chars.length <= max) return chars.join("");
  return `${chars.slice(0, max - 1).join("").trimEnd()}…`;
}

/**
 * Column widths for the scenes list, over the full bleed width. Seeing is the
 * longest text and gets the most room; a missing Product or Props column
 * hands its share to the others in proportion.
 */
function sceneWidths(withProduct: boolean, withProps: boolean): number[] {
  const shares: number[] = [SCENE_SHARES.seeing, SCENE_SHARES.hearing];
  if (withProduct) shares.push(SCENE_SHARES.product);
  if (withProps) shares.push(SCENE_SHARES.props);
  const total = shares.reduce((sum, share) => sum + share, 0);
  const textWidth = BLEED_WIDTH - SCENE_NUMBER_WIDTH - SCENE_NAME_WIDTH;
  const widths = [SCENE_NUMBER_WIDTH, SCENE_NAME_WIDTH, ...shares.map((share) => (share / total) * textWidth)];
  const rounded = widths.map(Math.round);
  // Absorb rounding so the columns add up to the bleed width exactly.
  rounded[rounded.length - 1]! += BLEED_WIDTH - rounded.reduce((sum, w) => sum + w, 0);
  return rounded;
}

interface LoadedImage {
  bytes: Uint8Array;
  info: ImageInfo;
}

function scriptBlocks(
  writer: DocxWriter,
  script: PersonaScriptDto,
  untitled: string,
  labels: ScriptDocxLabels,
  storyboard: readonly LoadedImage[],
): DocxBlock[] {
  const blocks: DocxBlock[] = [];
  const title = getPersonaScriptDisplayTitle(script, untitled);
  const product = trimmed(script.product);

  // Cover block: eyebrow (the client's product), title in the display face, the ad in one paragraph.
  const eyebrowParts = [product, labels.script].filter((part) => part.length > 0);
  blocks.push(writer.eyebrow(eyebrowParts.join("  ·  "), { after: 160 }));
  blocks.push(
    writer.text(title, {
      styleId: "Title",
      face: "display",
      size: SIZE.title,
      tracking: displayTracking(SIZE.title),
      line: LINE_TIGHT,
      after: 200,
    }),
  );
  const adDescription = trimmed(script.adDescription);
  if (adDescription) {
    blocks.push(writer.text(adDescription, { size: SIZE.lede, color: INK_2, after: 360 }));
  } else {
    blocks.push(writer.text("", { after: 200 }));
  }

  // Details: label eyebrows, plain values. Sequence last — it is derived from the scenes.
  const sequence =
    script.scenes.length > 0
      ? script.scenes.map((scene) => scene.sceneName).join("  →  ")
      : trimmed(script.sequence);
  const details: Array<readonly [string, string]> = [];
  if (trimmed(script.concept)) details.push([labels.concept, trimmed(script.concept)]);
  if (trimmed(script.duration)) details.push([labels.duration, trimmed(script.duration)]);
  if (trimmed(script.presenter)) details.push([labels.presenter, trimmed(script.presenter)]);
  if (trimmed(script.background)) details.push([labels.background, trimmed(script.background)]);
  if (product) details.push([labels.product, product]);
  if (sequence) details.push([labels.sequence, sequence]);
  if (details.length > 0) blocks.push(writer.detailList(details));

  // Reading order for a studio: the script first, then the sketch, then the shot list.
  const body = trimmed(script.script);
  if (body) {
    blocks.push(writer.sectionTitle(labels.script));
    blocks.push(...scriptBodyParagraphs(writer, body));
  }

  if (storyboard.length > 0) {
    blocks.push(writer.sectionTitle(labels.storyboard));
    storyboard.forEach(({ bytes, info }) => {
      blocks.push(writer.image(bytes, info, STORYBOARD_WIDTH_PX));
    });
  }

  if (script.scenes.length > 0) {
    blocks.push(writer.sectionTitle(labels.scenes));
    blocks.push(sceneList(writer, script.scenes, labels));
  }

  const hooks = script.hooks ?? [];
  if (hooks.length > 1) {
    blocks.push(writer.sectionTitle(labels.hooksTitle));
    blocks.push(hookList(writer, hooks, labels));
  }

  // The rationale ("Why this works") stays on screen only; the Word file is
  // the production handoff and the client does not need it (Ophir, 2026-11-01).
  return blocks;
}

function sceneList(
  writer: DocxWriter,
  scenes: readonly PersonaScriptSceneDto[],
  labels: ScriptDocxLabels,
): DocxTable {
  const withProduct = scenes.some((scene) => trimmed(scene.product));
  const withProps = scenes.some((scene) => trimmed(scene.props));
  const header = ["#", labels.scene, labels.seeing, labels.hearing];
  if (withProduct) header.push(labels.product);
  if (withProps) header.push(labels.props);
  const rows = [...scenes]
    .sort((a, b) => a.sceneNumber - b.sceneNumber)
    .map((scene): CellText[] => {
      const cells: CellText[] = [
        [[[String(scene.sceneNumber), { face: "medium", color: INK_2 }]]],
        [[[scene.sceneName, { face: "medium" }]]],
        proseCell(scene.seeing),
        proseCell(scene.hearing),
      ];
      if (withProduct) cells.push(proseCell(trimmed(scene.product)));
      if (withProps) cells.push(listCell(trimmed(scene.props)));
      return cells;
    });
  return writer.insetList(header, rows, sceneWidths(withProduct, withProps), { bleed: true });
}

/**
 * The script body, one paragraph per line. A beat marker like "[HOOK — 0-4s]"
 * becomes an ink eyebrow; a stage direction in parentheses recedes to the
 * label colour; a speaker tag before the first colon is set in the medium face.
 */
function scriptBodyParagraphs(writer: DocxWriter, body: string): DocxParagraph[] {
  const paragraphs: DocxParagraph[] = [];
  let pendingGap = false;
  for (const rawLine of body.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line.length === 0) {
      pendingGap = true;
      continue;
    }
    const first = paragraphs.length === 0;
    const beat = SCRIPT_BEAT_MARKER.exec(line);
    if (beat) {
      paragraphs.push(
        writer.eyebrow(beat[1]!.trim(), {
          color: INK,
          size: 16,
          before: first ? 0 : 400,
          after: 120,
          keepNext: true,
        }),
      );
      pendingGap = false;
      continue;
    }
    const before = pendingGap && !first ? 160 : 0;
    if (SCRIPT_STAGE_DIRECTION.test(line)) {
      paragraphs.push(writer.text(line, { color: INK_2, size: SIZE.cell, before, after: 110 }));
    } else {
      const speaker = SCRIPT_SPEAKER_LINE.exec(line);
      paragraphs.push(
        speaker
          ? writer.paragraph(
              [
                [`${speaker[1]!}: `, { face: "medium" }],
                [speaker[2]!],
              ],
              { before, after: 110 },
            )
          : writer.text(line, { before, after: 110 }),
      );
    }
    pendingGap = false;
  }
  return paragraphs;
}

function hookList(
  writer: DocxWriter,
  hooks: readonly PersonaScriptHookDto[],
  labels: ScriptDocxLabels,
): DocxTable {
  const ordered = [...hooks].sort((a, b) => a.position - b.position);
  const rows = ordered.map((hook, index): CellText[] => {
    const title = applyHookIndex(index === 0 ? labels.primaryHook : labels.hook, index + 1);
    const cell: CellText = [
      [[title, { face: "medium", color: INK_2, eyebrow: true, size: SIZE.eyebrow }]],
    ];
    return [cell, proseCell(trimmed(hook.voiceover)), proseCell(trimmed(hook.onScreenText))];
  });
  const withOnScreen = ordered.some((hook) => trimmed(hook.onScreenText));
  const header = ["", labels.voiceover];
  if (withOnScreen) header.push(labels.onScreenText);
  const label = 1900;
  const rest = CONTENT_WIDTH - label;
  const widths = withOnScreen ? [label, Math.round(rest * 0.6), rest - Math.round(rest * 0.6)] : [label, rest];
  return writer.insetList(
    header,
    rows.map((cells) => (withOnScreen ? cells : cells.slice(0, 2))),
    widths,
  );
}

/**
 * Prose in a cell, broken into paragraphs so a shot description can be read
 * at a glance: at line breaks, before "(1)"-style enumerators, and — when the
 * text is long and has no enumerators — into groups of sentences. A speaker
 * tag at the start of a paragraph is set in the medium face.
 */
function proseCell(text: string): CellText {
  const paragraphs = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .flatMap((line) => {
      const byEnumerator = line.split(CELL_ENUMERATOR_BREAK).map((part) => part.trim());
      if (byEnumerator.length > 1) return byEnumerator;
      return line.length > CELL_LONG_TEXT ? groupSentences(line) : [line];
    });
  if (paragraphs.length === 0) return [[["", {}]]];
  return paragraphs.map((paragraph): readonly Piece[] => {
    const speaker = SCRIPT_SPEAKER_LINE.exec(paragraph);
    if (!speaker) return [[paragraph]];
    return [
      [`${speaker[1]!}: `, { face: "medium" }],
      [speaker[2]!],
    ];
  });
}

function groupSentences(text: string): string[] {
  const sentences = text.split(CELL_SENTENCE_BREAK).map((part) => part.trim()).filter(Boolean);
  const groups: string[] = [];
  let current = "";
  for (const sentence of sentences) {
    // Close the group before this sentence would carry it past the target.
    if (current.length > 0 && current.length + 1 + sentence.length > CELL_PARAGRAPH_TARGET) {
      groups.push(current);
      current = sentence;
    } else {
      current = current.length === 0 ? sentence : `${current} ${sentence}`;
    }
  }
  if (current.length > 0) {
    // A short tail joins the previous group rather than standing alone, but
    // only while the result still reads as one short paragraph.
    const last = groups[groups.length - 1];
    if (
      last !== undefined &&
      current.length < CELL_PARAGRAPH_TARGET / 2 &&
      last.length + 1 + current.length <= CELL_LONG_TEXT
    ) {
      groups[groups.length - 1] = `${last} ${current}`;
    } else {
      groups.push(current);
    }
  }
  return groups.length > 0 ? groups : [text];
}

/**
 * A comma-separated list such as props, one item per line. Text with
 * sentence punctuation is prose and goes through `proseCell` instead.
 */
function listCell(text: string): CellText {
  const items = text
    .split(/,\s+|\r?\n/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  if (items.length < 2 || /[.!?]\s/.test(text)) return proseCell(text);
  return items.map((item): readonly Piece[] => [[item]]);
}

/** Reads the pixel size and type from a PNG, JPEG or GIF header. Null for anything else. */
export function imageInfo(bytes: Uint8Array): ImageInfo | null {
  if (bytes.length > 24 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return { type: "png", width: view.getUint32(16), height: view.getUint32(20) };
  }
  if (bytes.length > 10 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return { type: "gif", width: view.getUint16(6, true), height: view.getUint16(8, true) };
  }
  if (bytes.length > 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    let offset = 2;
    while (offset + 9 < bytes.length) {
      if (bytes[offset] !== 0xff) return null;
      const marker = bytes[offset + 1]!;
      const length = view.getUint16(offset + 2);
      // SOF0–SOF15 carry the frame size, except the DHT/JPG/DAC markers in that range.
      if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
        return { type: "jpg", height: view.getUint16(offset + 5), width: view.getUint16(offset + 7) };
      }
      offset += 2 + length;
    }
  }
  return null;
}

/** The ready sketch sheet(s) on a script: every model's image, in the order the API lists them. */
function storyboardImageUrls(script: PersonaScriptDto): string[] {
  if (script.storyboardStatus !== "READY" || !Array.isArray(script.storyboardImages)) return [];
  return script.storyboardImages.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const url = (item as { imageUrl?: unknown }).imageUrl;
    return typeof url === "string" && url.length > 0 ? [url] : [];
  });
}

async function loadStoryboard(
  script: PersonaScriptDto,
  loadImage: BuildDocxOptions["loadImage"],
): Promise<LoadedImage[]> {
  if (!loadImage) return [];
  const loaded = await Promise.all(
    storyboardImageUrls(script).map(async (url) => {
      try {
        const bytes = await loadImage(url);
        if (!bytes) return null;
        const info = imageInfo(bytes);
        return info && info.width > 0 && info.height > 0 ? { bytes, info } : null;
      } catch {
        return null;
      }
    }),
  );
  return loaded.filter((image): image is LoadedImage => image !== null);
}

/** A brief is free text: its first line becomes the title, the rest stay paragraphs. */
function briefBlocks(writer: DocxWriter, text: string, labels: ScriptDocxLabels): DocxBlock[] {
  const lines = text.split(/\r?\n/);
  const firstContent = lines.findIndex((line) => line.trim().length > 0);
  const titleStyle: ParagraphStyle = {
    styleId: "Title",
    face: "display",
    size: SIZE.title,
    tracking: displayTracking(SIZE.title),
    line: LINE_TIGHT,
    after: 320,
  };
  if (firstContent === -1) return [writer.text("", titleStyle)];
  const blocks: DocxBlock[] = [
    writer.eyebrow(labels.brief, { after: 160 }),
    writer.text(lines[firstContent]!.trim(), titleStyle),
  ];
  let pendingGap = false;
  for (const rawLine of lines.slice(firstContent + 1)) {
    const line = rawLine.trimEnd();
    if (line.trim().length === 0) {
      pendingGap = true;
      continue;
    }
    blocks.push(writer.text(line, { after: 110, before: pendingGap ? 160 : 0 }));
    pendingGap = false;
  }
  return blocks;
}

function partTitle(part: PersonaDocxPart): string {
  if (part.kind === "script") return getPersonaScriptDisplayTitle(part.script, part.untitled);
  return part.text.split(/\r?\n/).find((line) => line.trim().length > 0)?.trim() ?? "";
}

export async function buildPersonaDocumentsDocx(
  parts: readonly PersonaDocxPart[],
  options: BuildDocxOptions = {},
): Promise<Blob> {
  const docx = await import("docx");
  const { Document, Packer, SectionType } = docx;
  const writer = new DocxWriter(docx);

  const storyboards = await Promise.all(
    parts.map((part) => (part.kind === "script" ? loadStoryboard(part.script, options.loadImage) : [])),
  );

  const sections: DocxSection[] = parts.map((part, index) => {
    const labels = part.labels ?? DEFAULT_SCRIPT_DOCX_LABELS;
    const children =
      part.kind === "brief"
        ? briefBlocks(writer, part.text, labels)
        : scriptBlocks(writer, part.script, part.untitled, labels, storyboards[index] ?? []);
    return {
      properties: {
        type: index === 0 ? undefined : SectionType.NEXT_PAGE,
        page: {
          size: { width: PAGE.width, height: PAGE.height },
          margin: { ...MARGIN, footer: FOOTER_OFFSET },
        },
      },
      footers: { default: writer.footer(partTitle(part), labels.pageOf) },
      children,
    };
  });
  if (sections.length === 0) sections.push({ children: [writer.text("")] });

  const fonts: DocxDocumentFont[] = (options.fonts ?? []).map((font) => ({
    name: font.name,
    data: font.data as DocxDocumentFont["data"],
  }));

  const document = new Document({
    ...(fonts.length > 0 ? { fonts } : {}),
    styles: {
      default: {
        document: {
          run: { font: DOCX_FONT_BODY, size: SIZE.body, color: INK },
          paragraph: { spacing: { line: LINE_BODY } },
        },
      },
      paragraphStyles: [
        {
          id: "Title",
          name: "Title",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { font: DOCX_FONT_DISPLAY, size: SIZE.title, color: INK },
        },
        {
          id: "Heading1",
          name: "Heading 1",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { font: DOCX_FONT_MEDIUM, size: SIZE.section, color: INK },
          paragraph: { keepNext: true },
        },
      ],
    },
    sections,
  });
  const base64 = await Packer.toBase64String(document);
  return base64ToBlob(base64);
}

export async function downloadPersonaScriptDocx(
  script: PersonaScriptDto,
  untitled: string,
  labels?: ScriptDocxLabels,
  tenant?: string | null,
): Promise<void> {
  const blob = await buildPersonaDocumentsDocx([{ kind: "script", script, untitled, labels }], {
    fonts: await loadDocxFonts(),
    loadImage: fetchImageBytes,
  });
  downloadBlobAs(
    blob,
    personaDocumentFilename(
      { tenant, title: getPersonaScriptDisplayTitle(script, untitled) },
      "script",
      "docx",
    ),
  );
}

export async function downloadPersonaSelection(
  parts: readonly PersonaDocxPart[],
  format: PersonaDocumentExportFormat,
  filename: string,
): Promise<void> {
  if (format === "txt") {
    downloadText(formatPersonaExportText(parts, "txt"), filename, "text/plain;charset=utf-8");
    return;
  }
  const blob = await buildPersonaDocumentsDocx(parts, {
    fonts: await loadDocxFonts(),
    loadImage: fetchImageBytes,
  });
  downloadBlobAs(blob, filename);
}

function base64ToBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: DOCX_MIME });
}
